// Proof to bank on MyCoverageInfo (Yehuda, Oct 26): the portal cannot type into another site, so the
// step copies the three search values in one line and a "Fill MyCoverageInfo" bookmark, clicked on
// MyCoverageInfo's Agent Search page, fills them from the clipboard. The bookmark only reads the
// clipboard and types into that page: it never presses Search and sends nothing anywhere.
// Checked on the live Agent Search page on Oct 26 with made-up values: the page is Angular, the
// fields are #loanInput, #zipInput (5 characters) and #lastNameInput, and Angular takes the values.

/** The marker that starts the copied line, so the bookmark fills nothing from any other clipboard. */
export const mciMarker = 'SAFESIDE-MCI';

export type MciValues = { loan: string | null; zip: string | null; lastName: string | null };

/** The three values from the step's copy rows (spec C8 step 5), by their labels. */
export function mciValues(rows: { label: string; value: string | null }[]): MciValues {
  const of = (label: string) => rows.find((r) => r.label === label)?.value?.trim() || null;
  return { loan: of('Loan number'), zip: of('Property zip'), lastName: of('Borrower last name') };
}

/** "SAFESIDE-MCI|0012345678|10952|Sample"; a value not on the dec page stays empty. */
export const mciClipboard = (v: MciValues) =>
  [mciMarker, v.loan ?? '', (v.zip ?? '').slice(0, 5), v.lastName ?? ''].map(clean).join('|');

// A "|" or a line break inside a value would break the line apart.
const clean = (s: string) => s.replace(/[|\r\n]+/g, ' ').trim();

/**
 * What the bookmark runs, as plain JavaScript. It reads the clipboard (Chrome asks once to allow
 * it); if that is blocked or the clipboard holds something else, it asks the VA to paste. Each field
 * is found by its id, else by its label, and filled the way the page's own code sees typing. A field
 * it cannot find is named, so a change on MyCoverageInfo shows at once. A value the dec page did not
 * have clears its field and is named too, so the last search's zip or name never stays in the form.
 * (No comments inside the script: the bookmark is one line, so a // would end it.)
 */
export const fillScript = `(async () => {
  const marker = '${mciMarker}';
  let text = '';
  try { text = await navigator.clipboard.readText(); } catch (e) { text = ''; }
  if (!text.startsWith(marker + '|')) {
    text = window.prompt('Paste what the portal copied (Ctrl+V), then press OK.') || '';
  }
  const parts = text.trim().split('|');
  if (parts[0] !== marker || parts.length < 4) {
    window.alert('In the portal, press Copy for MyCoverageInfo first, then click this bookmark again.');
    return;
  }
  const fields = [
    ['loanInput', 'loan number', parts[1]],
    ['zipInput', 'zip', parts[2]],
    ['lastNameInput', 'last name', parts[3]],
  ];
  const missing = [];
  const empty = [];
  for (const [id, words, value] of fields) {
    const label = Array.from(document.querySelectorAll('label')).find((l) =>
      l.textContent.toLowerCase().includes(words));
    const el = document.getElementById(id) || (label && label.control);
    if (!el) { missing.push(words); continue; }
    if (!value) empty.push(words);
    const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), 'value').set;
    setter.call(el, value || '');
    for (const name of ['input', 'change', 'blur']) el.dispatchEvent(new Event(name, { bubbles: true }));
  }
  const notes = [];
  if (missing.length) notes.push('Could not find on this page: ' + missing.join(', ') + '.');
  if (empty.length) notes.push('Not found in the portal: ' + empty.join(', ') + '.');
  if (notes.length) window.alert(notes.join(' ') + ' Type it by hand.');
})();`;

/** The bookmark's address. */
export const fillBookmarklet = `javascript:${encodeURIComponent(fillScript.replace(/\n\s*/g, ' '))}`;

/** The Set up once note, Chrome on Windows. */
export const setUpOnce = [
  'Press Ctrl+Shift+B to show the bookmarks bar.',
  'Drag the Fill MyCoverageInfo button onto the bookmarks bar.',
  'On MyCoverageInfo, after Copy for MyCoverageInfo, click the bookmark. The first time, Chrome asks to see the clipboard: click Allow.',
];
